import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader, PlanTeaser, QuotaTeaser } from '../components/ui';
import { subscribeQuotaExceeded, type QuotaExceeded } from './quotaEvents';

// Discovery teaser host (story 2-1, P02 §5; 'quota' reason added in 2-16, P02
// §8). Any surface can call openTeaser(featureKey) — a locked nav row,
// settings row, tile — to surface the PlanTeaser with its plan CTA instead of
// routing to a page that 403s. Story 2-16 adds the quota reason: the apiClient
// response interceptor emits every quota_exceeded 402 (2-15's
// { reason:'quota', metric, used, limit, period } body) onto the quota bus,
// and this provider — subscribed below — opens the QuotaTeaser with the
// backend's real numbers, so hitting a cap never surfaces as a raw 402. The
// teaser primitives own the funnel events (feature.discovery impression/cta)
// and their CTAs (request access / upgrade), so every host — this modal or an
// inline teaser — gets the same working CTA.

type TeaserState =
  | { reason: 'feature'; featureKey: string; name?: string | undefined }
  | { reason: 'quota'; quota: QuotaExceeded };

interface DiscoveryContextValue {
  openTeaser: (featureKey: string, opts?: { name?: string }) => void;
  openQuotaTeaser: (quota: QuotaExceeded) => void;
}

const DiscoveryContext = createContext<DiscoveryContextValue | undefined>(undefined);

export function DiscoveryProvider({ children }: { children: ReactNode }): JSX.Element {
  const [teaser, setTeaser] = useState<TeaserState | null>(null);
  const navigate = useNavigate();

  const openTeaser = useCallback((featureKey: string, opts: { name?: string } = {}) => {
    setTeaser({ reason: 'feature', featureKey, name: opts.name });
  }, []);

  const openQuotaTeaser = useCallback((quota: QuotaExceeded) => {
    setTeaser({ reason: 'quota', quota });
  }, []);

  // Quota 402s from the shared axios instance land here (see apiClient's
  // response interceptor) — one subscription covers every capped call path.
  useEffect(() => subscribeQuotaExceeded(openQuotaTeaser), [openQuotaTeaser]);

  return (
    <DiscoveryContext.Provider value={{ openTeaser, openQuotaTeaser }}>
      {children}
      {/* No `title` on either ModalHeader below — `PlanTeaser`/`QuotaTeaser`
          are self-contained glass-card panels that already render their own
          visible heading; the header here supplies only the close
          affordance, not a second duplicate heading. No footer either — both
          primitives own their CTA button(s) inside their own card. */}
      {teaser?.reason === 'feature' && (
        <Modal label="Feature preview" onClose={() => setTeaser(null)}>
          <ModalHeader onClose={() => setTeaser(null)} />
          <ModalBody>
            <PlanTeaser feature={teaser.featureKey} {...(teaser.name ? { name: teaser.name } : {})} />
          </ModalBody>
        </Modal>
      )}
      {teaser?.reason === 'quota' && (
        <Modal label="Plan limit reached" onClose={() => setTeaser(null)}>
          <ModalHeader onClose={() => setTeaser(null)} />
          <ModalBody>
            <QuotaTeaser
              metric={teaser.quota.metric}
              used={teaser.quota.used}
              limit={teaser.quota.limit}
              period={teaser.quota.period}
              onUpgrade={() => {
                // Close the modal before routing so the focus-return doesn't
                // fight the page change; the primitive already sent the cta.
                setTeaser(null);
                navigate('/app/billing');
              }}
            />
          </ModalBody>
        </Modal>
      )}
    </DiscoveryContext.Provider>
  );
}

export function useDiscovery(): DiscoveryContextValue {
  const ctx = useContext(DiscoveryContext);
  if (!ctx) throw new Error('useDiscovery must be used within DiscoveryProvider');
  return ctx;
}
